'use client'

import React, { useState, useMemo, useCallback } from 'react'
import type { CampAnmeldung, Teilnehmer, Camp } from '@/payload-types'
import { Search } from 'lucide-react'
import { useDebounce } from '@/utilities/useDebounce'
import CampSelectionGrid from './CampSelectionGrid'
import FilteredAnmeldungenView from './FilteredAnmeldungenView'
import CampInfoHeader from './CampInfoHeader'

type PopulatedAnmeldung = CampAnmeldung & {
  teilnehmer: Teilnehmer
  camp: Camp
  alternativeCamp?: Camp | null
}

interface AnmeldungenViewWrapperProps {
  anmeldungen: PopulatedAnmeldung[]
  alternativeRegistrations?: PopulatedAnmeldung[]
  groupedAnmeldungen: Record<string, { camp: Camp; anmeldungen: PopulatedAnmeldung[] }>
  selectedCampId?: string
  searchTerm?: string
  emailSearch?: string
  // Bulk actions props
  selectedCount?: number
  onBulkStatusUpdate?: () => void
  onClearSelection?: () => void
  selectedAnmeldungen?: Set<string>
  onSelectAnmeldung?: (anmeldungId: string) => void
  onSelectAll?: () => void
}

const AnmeldungenViewWrapper: React.FC<AnmeldungenViewWrapperProps> = ({
  anmeldungen,
  alternativeRegistrations = [],
  groupedAnmeldungen,
  selectedCampId,
  searchTerm = '',
  emailSearch = '',
  selectedCount = 0,
  onBulkStatusUpdate,
  onClearSelection,
  selectedAnmeldungen = new Set(),
  onSelectAnmeldung = () => {},
  onSelectAll = () => {},
}) => {
  const [selectedCamps, setSelectedCamps] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [participantSearchTerm, setParticipantSearchTerm] = useState(emailSearch || '')

  const debouncedParticipantSearch = useDebounce(participantSearchTerm, 800)
  const debouncedSearchTerm = useDebounce(searchTerm, 800)

  // Update the availableCamps logic to include search filtering
  const availableCamps = useMemo(() => {
    if (selectedCampId) {
      return [groupedAnmeldungen[selectedCampId]?.camp].filter(Boolean) as Camp[]
    }

    let camps = Object.values(groupedAnmeldungen)
      .map((group) => group.camp)
      .filter(Boolean) as Camp[]

    if (!selectedCampId && debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase()
      camps = camps.filter((camp) => {
        const title = camp.title?.toLowerCase() || ''
        const location = camp.location?.toLowerCase() || ''
        return title.includes(searchLower) || location.includes(searchLower)
      })
    }

    return camps
  }, [groupedAnmeldungen, selectedCampId, debouncedSearchTerm])

  // Initialize selected camps
  React.useEffect(() => {
    if (selectedCamps.length === 0 && availableCamps.length > 0) {
      setSelectedCamps(availableCamps.map((c) => c.id))
    }
  }, [availableCamps, selectedCamps.length])

  const filteredAnmeldungen = useMemo(() => {
    let filtered = anmeldungen

    if (selectedCamps.length > 0) {
      filtered = filtered.filter((anmeldung) => selectedCamps.includes((anmeldung.camp as Camp).id))
    }

    if (statusFilter) {
      filtered = filtered.filter((anmeldung) => anmeldung.status === statusFilter)
    }

    const currentSearchTerm = selectedCampId ? debouncedParticipantSearch : debouncedSearchTerm

    if (currentSearchTerm.trim()) {
      const searchLower = currentSearchTerm.toLowerCase()
      filtered = filtered.filter((anmeldung) => {
        const camp = anmeldung.camp as Camp
        const teilnehmer = anmeldung.teilnehmer as Teilnehmer

        const campTitle = camp?.title?.toLowerCase() || ''
        const campLocation = camp?.location?.toLowerCase() || ''
        const teilnehmerName = `${teilnehmer?.firstName || ''} ${teilnehmer?.lastName || ''}`
          .trim()
          .toLowerCase()
        const teilnehmerEmail = teilnehmer?.email?.toLowerCase() || ''

        return (
          campTitle.includes(searchLower) ||
          campLocation.includes(searchLower) ||
          teilnehmerName.includes(searchLower) ||
          teilnehmerEmail.includes(searchLower)
        )
      })
    }

    return filtered
  }, [
    anmeldungen,
    selectedCamps,
    statusFilter,
    debouncedSearchTerm,
    debouncedParticipantSearch,
    selectedCampId,
  ])

  const filteredGroupedAnmeldungen = useMemo(() => {
    const filtered: Record<string, { camp: Camp; anmeldungen: PopulatedAnmeldung[] }> = {}

    if (!debouncedSearchTerm.trim()) {
      Object.entries(groupedAnmeldungen).forEach(([campId, campData]) => {
        if (selectedCamps.includes(campId)) {
          filtered[campId] = campData
        }
      })
      return filtered
    }

    const searchLower = debouncedSearchTerm.toLowerCase()

    Object.entries(groupedAnmeldungen).forEach(([campId, campData]) => {
      if (selectedCamps.includes(campId)) {
        const campAnmeldungen = campData.anmeldungen.filter((anmeldung) => {
          const camp = anmeldung.camp as Camp
          const teilnehmer = anmeldung.teilnehmer as Teilnehmer

          const campTitle = camp?.title?.toLowerCase() || ''
          const campLocation = camp?.location?.toLowerCase() || ''
          const teilnehmerName = `${teilnehmer?.firstName || ''} ${teilnehmer?.lastName || ''}`
            .trim()
            .toLowerCase()
          const teilnehmerEmail = teilnehmer?.email?.toLowerCase() || ''

          return (
            campTitle.includes(searchLower) ||
            campLocation.includes(searchLower) ||
            teilnehmerName.includes(searchLower) ||
            teilnehmerEmail.includes(searchLower)
          )
        })

        if (campAnmeldungen.length > 0) {
          filtered[campId] = {
            camp: campData.camp,
            anmeldungen: campAnmeldungen,
          }
        }
      }
    })

    return filtered
  }, [groupedAnmeldungen, selectedCamps, debouncedSearchTerm])

  const handleCampToggle = useCallback((campId: string) => {
    setSelectedCamps((prev) =>
      prev.includes(campId) ? prev.filter((id) => id !== campId) : [...prev, campId],
    )
  }, [])

  return (
    <div className="space-y-4">
      {/* Camp Info Header - only show for camp details */}
      {selectedCampId && groupedAnmeldungen[selectedCampId] && (
        <CampInfoHeader
          camp={groupedAnmeldungen[selectedCampId].camp}
          anmeldungen={anmeldungen}
          selectedStatus={statusFilter}
          onStatusFilter={setStatusFilter}
        />
      )}

      {/* Search Bar with Bulk Actions - only show for camp details */}
      {selectedCampId && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="E-Mail, Vorname oder Nachname suchen..."
                    value={participantSearchTerm}
                    onChange={(e) => setParticipantSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>
              </div>
              <div className="text-sm text-gray-600 whitespace-nowrap">
                {filteredAnmeldungen.length} Teilnehmer gefunden
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedCount > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-blue-900">
                  {selectedCount} ausgewählt
                </span>
                <button
                  onClick={onBulkStatusUpdate}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Status ändern
                </button>
                <button
                  onClick={onClearSelection}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-custom-blue focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Auswahl aufheben
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Camp Selection Grid - only show if not in detailed view */}
      {!selectedCampId && (
        <CampSelectionGrid
          camps={availableCamps}
          selectedCamps={selectedCamps}
          onCampToggle={handleCampToggle}
          groupedAnmeldungen={groupedAnmeldungen}
          emailSearch={emailSearch}
        />
      )}

      {/* Filtered Content - only show if in detailed view */}
      {selectedCampId && (
        <FilteredAnmeldungenView
          anmeldungen={filteredAnmeldungen}
          alternativeRegistrations={alternativeRegistrations}
          groupedAnmeldungen={filteredGroupedAnmeldungen}
          selectedAnmeldungen={selectedAnmeldungen}
          onSelectAnmeldung={onSelectAnmeldung}
          onSelectAll={onSelectAll}
          onBulkStatusUpdate={onBulkStatusUpdate}
          statusFilter={statusFilter}
          participantSearchTerm={participantSearchTerm}
          currentCampId={selectedCampId}
        />
      )}
    </div>
  )
}

export default AnmeldungenViewWrapper
